import TradeESIDSelect from "@/components/UI/TradeESIDSelect";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { returnCurrentTabStateJSON } from "@/lib/widgetHelperFunctions";
import { IActiveId, IWidgetDetails } from "@/lib/widgetsLayoutDir";
import { updateWorkspaceTabActionUpdate } from "@/store/workspacesTabsStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from "next-themes";
import React, { useEffect, useState } from "react";
import ActionSummaryTable from "../tradeActionSummary/ActionSummaryTable";

// LQC-1724 — the pop-out is its own browser window (and JS context/store), so it
// has no widget-grid cryptoID. The Trade Settings panel binding and the settings
// cog highlight are all keyed by cryptoID, so without one they never engage in the
// pop-out. Give the pop-out a stable synthetic key: it only has to be consistent
// within this window (the store is per-window), and the DB-save path only arms for
// cryptoIDs backed by a real openWidgets entry (LQC-1740), which this synthetic key
// never has, so it can't trigger any persistence.
const POPOUT_CRYPTO_ID = "popout";

interface ITradeTileHandler {
  cryptoID?: string | undefined;
  loadedFromPopout?: boolean;
  esidToPass: string;
  router: any;
  activeId?: Partial<IActiveId>; // Set to ? values as default - code such as popOut will not generate a activeId obj.
  tradeData: ExecutionStrategy[];
}

const TradeTileHandler: React.FC<ITradeTileHandler> = ({
  cryptoID,
  loadedFromPopout = false,
  esidToPass,
  router,
  activeId = {
    isActive: false,
    value: null,
    headerDragging: null,
    eventOver: "",
  },
  tradeData, // Set to null values as default - code such as popOut will not generate a activeId obj.
}) => {
  const [indexOfDefaultESID, setIndexOfDefaultESID] = useState<number>(-1);
  const { theme } = useTheme();

  const { getAccessTokenSilently, user } = useAuth0();

  let openWidgetsArr: IWidgetDetails[] | null = null;
  let openWidgetObject: IWidgetDetails | null = null;

  if (!loadedFromPopout) {
    const currentTab = returnCurrentTabStateJSON();
    openWidgetsArr = currentTab.openWidgets;
    openWidgetObject = currentTab.openWidgets.find(
      (widget: any) => widget.i == cryptoID,
    );
  }

  //TO DO - one central location for all store checks and database fetching.
  const [selectedESID, setSelectedESID] = useState("");
  const [ESIDprops, setESIDprops] = useState<any>();

  // get a list of all ES id's to choose from.
  const [ESIDArr, setESIDArr] = useState<IESIDArr[]>(
    tradeData.map((es) => {
      return { label: es.esid, value: es.esid };
    }),
  );

  function callActionSummaryHelper() {
    if (loadedFromPopout) {
      if (esidToPass && selectedESID == "") {
        const indexValue = ESIDArr.map((e) => e.value).indexOf(esidToPass);
        setIndexOfDefaultESID(indexValue); // Pop out working
        setSelectedESID(esidToPass);
        const data = tradeData.find((es) => es.esid == esidToPass);
        setESIDprops(data);
      }
    } else {
      if (openWidgetObject && openWidgetObject.esid != undefined) {
        const indexValue = ESIDArr.map((e) => e.value).indexOf(
          openWidgetObject.esid,
        );
        setIndexOfDefaultESID(indexValue); // Working when loading from the main page.
        setSelectedESID(openWidgetObject.esid);
        const data = tradeData.find((es) => es.esid == openWidgetObject!.esid);
        setESIDprops(data);
      } else {
        setESIDprops(null);
        setSelectedESID("");
      }
    }
  }

  useEffect(() => {
    callActionSummaryHelper();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    callActionSummaryHelper();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeData]);

  //React Select for Choosing esid:
  const [isClearable] = useState(true);
  const [isSearchable] = useState(true);
  const [isDisabled] = useState(false);
  // const [isLoading] = useState(false);
  const [isRtl] = useState(false);

  interface IESIDArr {
    label: string | undefined;
    value: string | undefined;
  }

  //
  useEffect(() => {
    if (activeId.eventOver != "") {
      // Only update the widget that the trade was dropped over
      if (activeId.eventOver == cryptoID) {
        handleSelectedESID({
          label: activeId!.value!.toString(),
          value: activeId!.value!.toString(),
        });
        setSelectedESID(activeId!.value!.toString());
        setReactSelectViaTradeIndex(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const handleSelectedESID = (esidPassed: IESIDArr | null) => {
    async function ifESIDAddToOpenWidgets(isESIDPresent: boolean) {
      // openWidgetsArr is only getting called when loadedFromPopout is false. No hence openWidgetsArr!
      const arrWithUpdatedOpenWidgetData = openWidgetsArr!.map((widget: any) => {
        if (openWidgetObject && widget.i == openWidgetObject.i) {
          if (isESIDPresent) {
            return { ...widget, esid: esidPassed?.value };
          } else {
            return { ...widget, esid: null };
          }
        }
        return widget;
      });

      const currentTabStateJSON = returnCurrentTabStateJSON();

      updateWorkspaceTabActionUpdate(
        arrWithUpdatedOpenWidgetData,
        currentTabStateJSON,
        getAccessTokenSilently,
        true,
      );
    }

    if (esidPassed && esidPassed.value != undefined) {
      setSelectedESID(esidPassed.value);
      if (!loadedFromPopout) {
        ifESIDAddToOpenWidgets(true); // Don't try and do a database call if working in the pop out.
      }
    } else {
      setESIDprops("");
      setSelectedESID("");
      // LQC-1607: reset the dropdown to its placeholder immediately on clear (X).
      // Lives here (not in the sync effect below) because the effect can't tell a
      // cleared selection from one that hasn't been restored yet (LQC-1980).
      setIndexOfDefaultESID(-1);
      if (!loadedFromPopout) {
        ifESIDAddToOpenWidgets(false);
      }
    }
  };

  // Helper function to call the placeholder of react select in multiple places.
  function setReactSelectViaTradeIndex(updatedViaDragAndDrop: boolean) {
    let index = 0;

    if (updatedViaDragAndDrop && activeId.value) {
      index = ESIDArr.map((ele) => {
        return ele.value;
      }).indexOf(activeId.value.toString());
    } else {
      index = ESIDArr.map((ele) => {
        return ele.value;
      }).indexOf(selectedESID);
    }
    setIndexOfDefaultESID(index);
  }

  useEffect(() => {
    // Update the arr in case a new trade has come in.
    const updatedESIDArr = tradeData.map((es) => {
      return { label: es.esid, value: es.esid };
    });
    setESIDArr(updatedESIDArr);

    if (selectedESID) {
      // Look up against the freshly built array, not the ESIDArr state (stale in
      // this closure), so the index is right the first time trade data lands.
      setIndexOfDefaultESID(
        updatedESIDArr.findIndex((e) => e.value == selectedESID),
      );
      setESIDprops(tradeData.find((es) => es.esid == selectedESID));
    } else if (loadedFromPopout || !openWidgetObject?.esid) {
      setSelectedESID("");
      // Reset the dropdown index so the Select falls back to its placeholder
      // when there is no selection to restore.
      setIndexOfDefaultESID(-1);
    }
    // LQC-1980: when selectedESID is "" but the widget still has a persisted esid,
    // the restore in callActionSummaryHelper hasn't committed yet (this effect runs
    // in the same commit with a stale closure). Skipping the reset in that case
    // stops this effect wiping the restored selection and index on load.

    if (loadedFromPopout) {
      // if the tile is loaded form the PopOut, goal is to update the URL so the tile can remember it's state.
      if (esidToPass && selectedESID) {
        router.push(`/Popout?component=TradeTile&esid=${selectedESID}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeData, selectedESID]);

  return (
    <div
      // MUST have a class of GridContainer to allow the pop up parent to render into.
      data-testid="GridContainer"
      className={`GridContainer h-full w-full bg-cubeBase-500 pt-1 dark:bg-cubeBaseDark-500`}
    >
      <div className="flex h-[calc(100%)] w-full flex-col pt-1">
        <div className="flex-0 flex h-fit w-full flex-wrap items-center">
          <div className="flex w-full flex-1 items-center justify-between">
            <TradeESIDSelect
              options={ESIDArr}
              value={
                indexOfDefaultESID != -1 ? ESIDArr[indexOfDefaultESID!] : null
              }
              onChange={handleSelectedESID}
              theme={theme}
              isDisabled={isDisabled}
              isClearable={isClearable}
              isSearchable={isSearchable}
              isRtl={isRtl}
              name="color"
            />
          </div>
        </div>
        <div className="px h-full w-full py-2 pr-1">
          <ActionSummaryTable
            cryptoID={loadedFromPopout ? POPOUT_CRYPTO_ID : cryptoID}
            getAccessTokenSilently={getAccessTokenSilently}
            user={user}
            isMiniTable={true}
            dataForMainTable={ESIDprops ? ESIDprops : []}
            tradeData={ESIDprops ? tradeData : []}
          />
        </div>
      </div>
    </div>
  );
};

export default TradeTileHandler;
